/**
 * ServicingDashboard — Main container for the Interim Servicing pipeline.
 *
 * Shows summary stat cards, a status filter toggle, and the loan table.
 * Clicking a loan row opens a detail Sheet (slide-over panel).
 */

import { useState, useMemo } from 'react';
import {
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Loader2,
  Mail,
  Download,
  Search,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/useAuth';
import { createComponentLogger } from '@/utils/logger';
import { formatCurrency } from '@/components/banker-recon/types';
import {
  useLoanOfficerServicingLoans,
  useCompletedServicingPayments,
  useServicingLoans,
  type ServicingStatusCounts
} from '@/hooks/useServicing';
import {
  getPaymentReminderCandidates,
  getPaymentsForLoans,
  sendPaymentReminders
} from '@/services/servicingService';
import type {
  CompletedServicingPayment,
  ServicingLoan,
  ServicingLoanStatus,
  PaymentReminderLoan
} from '@/types/servicing';
import { exportServicingWorkbook } from '@/utils/servicing/servicingExport';
import { ReportDateRangePicker } from '@/components/reports/date-range/ReportDateRangePicker';
import {
  fmtRange,
  makePreset,
  type ReportPeriod
} from '@/components/reports/date-range/reportPeriod';
import { ServicingTable } from './ServicingTable';
import { CompletedPaymentsTable } from './CompletedPaymentsTable';
import { ServicingLoanDetail } from './ServicingLoanDetail';
import { PaymentReminderConfirmDialog } from './PaymentReminderConfirmDialog';

const log = createComponentLogger('ServicingDashboard', 'servicing');

// ---------------------------------------------------------------------------
// Status filter options
// ---------------------------------------------------------------------------

type FilterOption = { label: string; value: ServicingLoanStatus | undefined };

const ACCOUNTING_FILTER_OPTIONS: FilterOption[] = [
  { label: 'Active', value: 'active' },
  { label: 'Completed Payments', value: 'completed' },
  { label: 'Transferred', value: 'transferred' },
  { label: 'All', value: undefined }
];

const LO_FILTER_OPTIONS: FilterOption[] = [
  { label: 'All', value: undefined },
  { label: 'Active', value: 'active' },
  { label: 'Completed', value: 'completed' },
  { label: 'Transferred', value: 'transferred' }
];

function normalizeSearchValue(value: unknown): string {
  return String(value ?? '').toLowerCase();
}

function getStatusCount(
  counts: ServicingStatusCounts | undefined,
  status: ServicingLoanStatus | undefined
): number {
  if (!counts) {
    return 0;
  }
  return status ? counts[status] : counts.all;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ServicingDashboardMode = 'accounting' | 'loan-officer';

interface ServicingDashboardProps {
  mode?: ServicingDashboardMode;
}

export function ServicingDashboard({ mode = 'accounting' }: ServicingDashboardProps) {
  const { user } = useAuth();
  const organizationId = user?.organization_id;
  const isLoanOfficerMode = mode === 'loan-officer';

  const accountingLoans = useServicingLoans(isLoanOfficerMode ? undefined : organizationId);
  const ownLoans = useLoanOfficerServicingLoans(isLoanOfficerMode);

  const {
    loans,
    metrics,
    loading,
    error,
    refresh,
    statusFilter,
    setStatusFilter
  } = isLoanOfficerMode ? ownLoans : accountingLoans;

  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [completedPeriod, setCompletedPeriod] = useState<ReportPeriod>(() => makePreset('ytd'));
  const [exporting, setExporting] = useState(false);

  const isCompletedPaymentsView = !isLoanOfficerMode && statusFilter === 'completed';
  const completedPayments = useCompletedServicingPayments(
    organizationId,
    isCompletedPaymentsView
  );

  // Payment reminder state
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderCandidates, setReminderCandidates] = useState<PaymentReminderLoan[]>([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // Derive the currently selected loan object for the sheet title
  const selectedLoan = useMemo(
    () => loans.find((l) => l.id === selectedLoanId) ?? null,
    [loans, selectedLoanId]
  );

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const isSearchActive = normalizedSearch.length > 0;

  const filteredLoans = useMemo(() => {
    if (!isSearchActive) {
      return loans;
    }

    return loans.filter((loan) =>
      [
        loan.borrower_name,
        loan.loan_number,
        loan.investor_name
      ].some((value) => normalizeSearchValue(value).includes(normalizedSearch))
    );
  }, [isSearchActive, loans, normalizedSearch]);

  const filteredCompletedPayments = useMemo(() => {
    const from = format(completedPeriod.from, 'yyyy-MM-dd');
    const to = format(completedPeriod.to, 'yyyy-MM-dd');
    return completedPayments.payments.filter((row) => {
      const receivedDate = row.payment.payment_received_date;
      if (!receivedDate || receivedDate < from || receivedDate > to) {
        return false;
      }
      if (!isSearchActive) {
        return true;
      }
      return [
        row.loan.borrower_name,
        row.loan.loan_number,
        row.loan.investor_name
      ].some((value) => normalizeSearchValue(value).includes(normalizedSearch));
    });
  }, [completedPayments.payments, completedPeriod, isSearchActive, normalizedSearch]);

  const tableLoading = loading && !(isSearchActive && loans.length > 0);
  const statusCounts = isLoanOfficerMode ? ownLoans.statusCounts : undefined;
  const filterOptions = isLoanOfficerMode ? LO_FILTER_OPTIONS : ACCOUNTING_FILTER_OPTIONS;
  const allOwnedCount = statusCounts?.all ?? 0;
  const selectedStatusCount = getStatusCount(statusCounts, statusFilter);
  const selectedStatusLabel =
    ACCOUNTING_FILTER_OPTIONS.find((opt) => opt.value === statusFilter)?.label.toLowerCase() ?? 'selected';
  const showLoanOfficerStatusEmpty =
    isLoanOfficerMode &&
    !isSearchActive &&
    ownLoans.profileLinked !== false &&
    !!statusFilter &&
    allOwnedCount > 0 &&
    selectedStatusCount === 0;
  const showLoanOfficerNoRecordsEmpty =
    isLoanOfficerMode &&
    !isSearchActive &&
    ownLoans.profileLinked !== false &&
    allOwnedCount === 0;

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleSelectLoan = (loan: ServicingLoan) => {
    log.info('Loan selected', { loanId: loan.id, loanNumber: loan.loan_number });
    setSelectedLoanId(loan.id);
    setSheetOpen(true);
  };

  const handleSheetClose = (open: boolean) => {
    setSheetOpen(open);
    if (!open) {
      setSelectedLoanId(null);
    }
  };

  const handleRefresh = async () => {
    log.info('Manual refresh triggered');
    await Promise.all([refresh(), completedPayments.refresh()]);
  };

  const handleDetailRefresh = async () => {
    // Refresh the list so stat cards stay in sync after a payment is recorded
    await Promise.all([refresh(), completedPayments.refresh()]);
  };

  const handleExport = async () => {
    if (isLoanOfficerMode) {
      return;
    }

    const exportLoans = isCompletedPaymentsView
      ? [...new Map(filteredCompletedPayments.map((row) => [row.loan.id, row.loan])).values()]
      : filteredLoans.filter((loan) => loan._source !== 'encompass_loans');

    if (exportLoans.length === 0) {
      toast.info('No servicing records match the current filters.');
      return;
    }

    setExporting(true);
    try {
      const history = await getPaymentsForLoans(exportLoans.map((loan) => loan.id));
      const loansById = new Map(exportLoans.map((loan) => [loan.id, loan]));
      const filteredReceipts: CompletedServicingPayment[] = isCompletedPaymentsView
        ? filteredCompletedPayments
        : history
          .filter((payment) => payment.status === 'received' && payment.payment_received_date)
          .flatMap((payment) => {
            const loan = loansById.get(payment.servicing_loan_id);
            return loan ? [{ payment, loan }] : [];
          });

      await exportServicingWorkbook({
        loans: exportLoans,
        filteredReceipts,
        paymentHistory: history,
        dateLabel: isCompletedPaymentsView ? fmtRange(completedPeriod) : 'All payment dates',
        statusLabel: ACCOUNTING_FILTER_OPTIONS.find((option) => option.value === statusFilter)?.label ?? 'All'
      });
      toast.success('Servicing workbook exported.');
    } catch (err) {
      log.error('Failed to export servicing workbook', {
        metadata: { error: err instanceof Error ? err.message : String(err) }
      });
      toast.error('Failed to export servicing workbook.');
    } finally {
      setExporting(false);
    }
  };

  const handleBulkReminder = async () => {
    if (isLoanOfficerMode) {
      return;
    }
    if (!organizationId) {
      return;
    }
    setLoadingCandidates(true);
    try {
      const candidates = await getPaymentReminderCandidates(organizationId);
      if (candidates.length === 0) {
        toast.info('No servicing loans currently have a pending payment reminder.');
        return;
      }
      setReminderCandidates(candidates);
      setReminderDialogOpen(true);
    } catch (err) {
      log.error('Failed to fetch reminder candidates', { error: err });
      toast.error('Failed to check for due payments.');
    } finally {
      setLoadingCandidates(false);
    }
  };

  const handleConfirmSendReminders = async (selectedLoans: PaymentReminderLoan[]) => {
    setBulkSending(true);
    try {
      const result = await sendPaymentReminders(selectedLoans);
      if (!result.success) {
        toast.error(result.error || 'Failed to send reminders.');
        return;
      }
      const { sent = 0, skipped = 0, failed = 0, total = 0 } = result.summary || {};
      const skipCopy = 'no borrower email or upcoming duplicate';
      if (total > 0 && failed === total) {
        toast.error('All reminders failed to send.');
      } else if (sent > 0 && failed > 0 && skipped > 0) {
        toast.warning(`Sent ${sent}, ${skipped} skipped, ${failed} failed.`);
      } else if (sent > 0 && failed > 0) {
        toast.warning(`Sent ${sent}, ${failed} failed to deliver.`);
      } else if (sent > 0 && skipped > 0) {
        toast.warning(`Sent ${sent}, ${skipped} skipped (${skipCopy}).`);
      } else if (sent > 0) {
        toast.success(`Payment reminders sent to ${sent} borrower${sent !== 1 ? 's' : ''}.`);
      } else if (failed > 0) {
        toast.error(`${failed} reminder${failed !== 1 ? 's' : ''} failed to send.`);
      } else if (skipped > 0) {
        toast.warning(`All ${skipped} reminder${skipped !== 1 ? 's' : ''} skipped (${skipCopy}).`);
      }
    } catch (err) {
      log.error('Failed to send payment reminders', { error: err });
      toast.error('Failed to send payment reminders.');
    } finally {
      setBulkSending(false);
      setReminderDialogOpen(false);
      setReminderCandidates([]);
    }
  };

  // -----------------------------------------------------------------------
  // Stat cards data
  // -----------------------------------------------------------------------

  const statCards = [
    {
      title: 'Active Loans',
      value: metrics?.totalActiveLoans ?? 0,
      formatted: String(metrics?.totalActiveLoans ?? 0),
      icon: CheckCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: 'Outstanding',
      value: metrics?.totalOutstanding ?? 0,
      formatted: formatCurrency(metrics?.totalOutstanding ?? 0),
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Collected',
      value: metrics?.totalCollected ?? 0,
      formatted: formatCurrency(metrics?.totalCollected ?? 0),
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    ...(isLoanOfficerMode ? [] : [{
      title: 'Net Servicing Income',
      value: metrics?.netServicingIncome ?? 0,
      formatted: formatCurrency(metrics?.netServicingIncome ?? 0),
      icon: TrendingUp,
      color: 'text-violet-600',
      bgColor: 'bg-violet-50'
    }])
  ];

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Interim Servicing</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isLoanOfficerMode
              ? 'View your servicing records and retrieve borrower documents'
              : 'Track loans in interim servicing and record borrower payments'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isLoanOfficerMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={loading || completedPayments.loading || exporting}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export
            </Button>
          )}
          {!isLoanOfficerMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkReminder}
              disabled={loading || loadingCandidates || bulkSending}
            >
              {loadingCandidates ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              Send Payment Reminders
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {(error || completedPayments.error) && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error || completedPayments.error}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.title}
              </CardTitle>
              <div className={`rounded-md p-2 ${card.bgColor}`}>
                <card.icon className={`h-4 w-4 ${card.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.formatted}</div>
              {card.title === 'Active Loans' && metrics && metrics.overduePaymentsCount > 0 && (
                <p className="text-xs text-destructive mt-1">
                  {metrics.overduePaymentsCount} overdue payment{metrics.overduePaymentsCount !== 1 ? 's' : ''}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Status Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search borrower, loan #, investor..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 bg-background"
          />
          {searchQuery && (
            <button
              type="button"
              aria-label="Clear servicing search"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground mr-1">Status:</span>
          {filterOptions.map((opt) => (
            <Badge
              key={opt.label}
              variant={statusFilter === opt.value ? 'default' : 'outline'}
              className="cursor-pointer select-none"
              onClick={() => setStatusFilter(opt.value)}
            >
              {isLoanOfficerMode
                ? `${opt.label} (${getStatusCount(statusCounts, opt.value)})`
                : opt.label}
            </Badge>
          ))}
        </div>

        {isCompletedPaymentsView && (
          <ReportDateRangePicker
            value={completedPeriod}
            onChange={setCompletedPeriod}
            max={new Date()}
          />
        )}
      </div>

      {/* Loan Table */}
      {isCompletedPaymentsView ? (
        <CompletedPaymentsTable
          rows={filteredCompletedPayments}
          loading={completedPayments.loading}
          onSelectLoan={(row) => handleSelectLoan(row.loan)}
        />
      ) : (
        <ServicingTable
          loans={filteredLoans}
          loading={tableLoading}
          onSelectLoan={handleSelectLoan}
          emptyTitle={
            isLoanOfficerMode && ownLoans.profileLinked === false
              ? 'Your profile is not linked to Encompass'
              : isSearchActive ? 'No servicing loans match this search'
              : showLoanOfficerStatusEmpty ? `No ${selectedStatusLabel} servicing records`
              : showLoanOfficerNoRecordsEmpty ? 'No servicing records found'
              : undefined
          }
          emptyDescription={
            isLoanOfficerMode && ownLoans.profileLinked === false
              ? 'Ask an administrator to link your user profile to your Encompass user ID before servicing records can be shown.'
              : isSearchActive ? 'Try a different borrower, loan number, or investor.'
              : showLoanOfficerStatusEmpty
                ? `You have ${allOwnedCount} record${allOwnedCount !== 1 ? 's' : ''} in All.`
              : showLoanOfficerNoRecordsEmpty
                ? 'Servicing records will appear here when purchased loans are assigned to you.'
              : undefined
          }
          emptyAction={
            showLoanOfficerStatusEmpty ? (
              <Button variant="outline" size="sm" onClick={() => setStatusFilter(undefined)}>
                Show all
              </Button>
            ) : undefined
          }
        />
      )}

      {/* Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={handleSheetClose}>
        <SheetContent
          side="right"
          className="w-[60vw] sm:max-w-[60vw] overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>
              {selectedLoan
                ? `Loan ${selectedLoan.loan_number}`
                : 'Loan Detail'}
            </SheetTitle>
            <SheetDescription>
              {selectedLoan?.borrower_name ?? 'Servicing detail and amortization schedule'}
            </SheetDescription>
          </SheetHeader>

          {selectedLoanId ? (
            <ServicingLoanDetail
              servicingLoanId={selectedLoanId}
              virtualLoan={selectedLoan?._source === 'encompass_loans' ? selectedLoan : undefined}
              onPaymentRecorded={handleDetailRefresh}
              mode={mode}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Payment Reminder Confirm Dialog */}
      <PaymentReminderConfirmDialog
        open={reminderDialogOpen}
        onOpenChange={setReminderDialogOpen}
        candidates={reminderCandidates}
        onConfirm={handleConfirmSendReminders}
        sending={bulkSending}
      />
    </div>
  );
}
